import { ModuleTypes } from 'dashboard/apps/DashboardLayoutEditor/constants';
import { FormUpdateTagsData } from 'dashboard/forms/FormUpdateTags';
import { Module } from 'dashboard/lib/module';
import { Environment } from 'dashboard/models/environment';
import { List } from 'dashboard/models/list';
import { ListItem } from 'dashboard/models/list-item';
import { ListMatch } from 'dashboard/models/list-match';
import { Tag } from 'dashboard/models/tag';
import { Transaction } from 'dashboard/models/transaction';
import { TransactionType } from 'dashboard/models/transaction-type';
import { User } from 'dashboard/models/user';
import DataDisplay from 'dashboard/modules/DataDisplay';
import EventTimeline from 'dashboard/modules/EventTimeline';
import { ModuleListMatches } from 'dashboard/modules/ListMatches';
import TransactionListMatches from 'dashboard/modules/ListMatches/TransactionListMatches';
import Markdown from 'dashboard/modules/Markdown';
import Relations from 'dashboard/modules/Relations';
import ResourceV2 from 'dashboard/modules/Resource/ResourceV2';
import { ModuleResourceInquiryV2 } from 'dashboard/modules/ResourceInquiryV2';
import { Tags } from 'dashboard/modules/Tags/Tags';
import { TransactionState } from 'dashboard/redux/slices/transaction';
import { TransactionDataContextProvider } from 'dashboard/routes/DashboardTransactionsShowRoute/useTransactionDataContext';

export interface TransactionApi {
  transaction: Transaction;
  transactionType: TransactionType;
  currentEnvironment: Environment;
  transactionVariableDefinitions: any[];
  relations: TransactionState['relations'];
  tags: Tag[];
  users: User[];
  handleUpdateTags: (tags: FormUpdateTagsData) => void;
  lists: Record<string, List>;
  listItems: Record<string, ListItem>;
  listMatches: Record<string, ListMatch>;
}

export interface TransactionModuleBaseProps<M extends Module> {
  module: M;
  transactionApi: TransactionApi;
  isPreview?: boolean;
}

export function TransactionModuleBase<M extends Module>({
  module,
  transactionApi,
  isPreview = false,
}: TransactionModuleBaseProps<M>) {
  const {
    transaction,
    transactionVariableDefinitions,
    tags,
    lists,
    listItems,
    listMatches,
    relations,
    currentEnvironment,
    handleUpdateTags,
  } = transactionApi;

  const renderModule = () => {
    switch (module.type) {
      case ModuleTypes.Markdown:
        return (
          <TransactionDataContextProvider
            transactionVariableDefinitions={transactionVariableDefinitions}
            transactionId={transaction.id}
          >
            {(data) => <Markdown module={module} templateData={data} isPreview={isPreview} />}
          </TransactionDataContextProvider>
        );
      case ModuleTypes.DataDisplay:
        return (
          <TransactionDataContextProvider
            transactionVariableDefinitions={transactionVariableDefinitions}
            transactionId={transaction.id}
          >
            {(data) => <DataDisplay module={module} data={data} isPreview={isPreview} />}
          </TransactionDataContextProvider>
        );
      case ModuleTypes.Resource: {
        return (
          <TransactionDataContextProvider
            transactionVariableDefinitions={transactionVariableDefinitions}
            transactionId={transaction.id}
          >
            {(data) => (
              <ResourceV2
                module={module as ModuleResourceInquiryV2}
                currentObjectToken={transaction.id}
                resources={data}
              />
            )}
          </TransactionDataContextProvider>
        );
      }
      case ModuleTypes.Tags: {
        return <Tags tags={tags} handleUpdateTags={handleUpdateTags} />;
      }
      case ModuleTypes.EventTimeline: {
        return (
          <EventTimeline
            module={module}
            subject={transaction}
            objects={[]}
            currentEnvironment={currentEnvironment}
            isPreview={isPreview}
          />
        );
      }
      case ModuleTypes.ListMatches: {
        return (
          <TransactionListMatches
            lists={lists}
            listItems={listItems}
            listMatches={listMatches}
            module={module as ModuleListMatches}
            isLoading={false}
          />
        );
      }
      case ModuleTypes.Relations:
        return <Relations relations={relations} module={module} />;

      default:
        return null;
    }
  };
  return <>{renderModule()}</>;
}
